/**
 * The packet: choose what you want, get a PDF.
 *
 * This page used to *be* the packet — an 8-inch column of HTML that the
 * browser printed. Two engines disagreed about that page often enough that
 * the fixes had become a list of workarounds rather than a design, which is
 * the same thing that happened to the sector maps. Steward writes the
 * document itself now, on the same page furniture, so a map sheet and a
 * sector roster in the same binder look like one system. See incident/packet.ts.
 *
 * What is left here is the order form.
 */
import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import {
  useFilterSearch, useIncidentBoard, useParcels,
  type IncidentSite, type SearchPeopleRow, type SectorAssignment,
} from '../api';
import { OPEN_CALLINGS_FILTER, leadershipFrom } from '../incident/leadership';
import {
  SECTIONS, buildPacketPdf, packetFilename,
  type PacketSections, type PacketSector, type Section,
} from '../incident/packet';
import { layerLabel, sliceById } from '../incidentSlices';
import { stripHtml } from '../incidentVocab';
import { openBlob, releaseTab, reserveTab } from '../pdf/download';
import { WARD_NAME } from '../plat/document';
import type { PlatData } from '../plat/model';

/** The layers a sector sheet can be cut on, and which is the lead's. */
const MACRO = 'macro';
const MICRO = 'micro';

export default function IncidentPacket() {
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const { data, isLoading, error } = useIncidentBoard(id);
  const parcels = useParcels();
  const leaders = useFilterSearch('people', OPEN_CALLINGS_FILTER as never, { limit: 500 });

  const [sections, setSections] = useState<PacketSections>(
    () => Object.fromEntries(SECTIONS.map((s) => [s, true])) as PacketSections);
  const [busy, setBusy] = useState<{ done: number; total: number; label: string } | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  const layer = params.get('layer') || MICRO;
  const sectorFilter = params.get('sector');
  const slice = sliceById(params.get('slice'));

  /**
   * Sites grouped into the sheets they will be printed as.
   *
   * Cut on walks — the default, because a walk is what somebody carries — the
   * packet nests: each sector gets its own map, then the walks inside it get
   * a map and a roster each. Cut on sectors, a sector is a leaf and carries
   * its own roster. Either way both layers decide who is named on a sheet:
   * the sector's lead and the walk's walker are different people.
   */
  const sectors: PacketSector[] = useMemo(() => {
    const visible = (data?.sites ?? []).filter((s) => {
      if (s.excluded) return false;                // skipped addresses never reach paper
      if (slice && !slice.match(s)) return false;
      return true;
    });

    const find = (l: string, name: string | null): SectorAssignment | undefined =>
      (name ? data?.assignments.find((a) => a.layer === l && a.sector === name) : undefined);

    // Only when every address in a group agrees: a walk that straddles two
    // sectors has no single lead, and printing one of them would be worse
    // than printing a rule.
    const agreed = (sites: IncidentSite[], l: string) => {
      const values = new Set(sites.map((s) => s.sectors?.[l]?.trim()).filter(Boolean));
      return values.size === 1 ? [...values][0] as string : null;
    };

    const groupBy = (sites: IncidentSite[], l: string) => {
      const groups = new Map<string, IncidentSite[]>();
      for (const s of sites) {
        const key = s.sectors?.[l]?.trim() || 'Unassigned';
        const list = groups.get(key);
        if (list) list.push(s); else groups.set(key, [s]);
      }
      return [...groups.entries()].sort(([a], [b]) =>
        (a === 'Unassigned' ? 1 : b === 'Unassigned' ? -1 : a.localeCompare(b)));
    };

    const sheet = (name: string, sites: IncidentSite[]): PacketSector => {
      const lead = find(MACRO, agreed(sites, MACRO));
      const walk = find(MICRO, agreed(sites, MICRO));
      return {
        name,
        sites,
        assignments: [
          { role: 'Lead', name: lead?.assigned_to ?? null, phone: lead?.phone ?? null,
            radio: lead?.radio_channel ?? null },
          { role: 'Walking', name: walk?.assigned_to ?? null, phone: walk?.phone ?? null,
            radio: walk?.radio_channel ?? null },
        ],
      };
    };

    if (layer === MACRO) {
      return groupBy(visible, MACRO)
        .filter(([name]) => !sectorFilter || name === sectorFilter)
        .map(([name, sites]) => sheet(name, sites));
    }

    return groupBy(visible, MACRO).map(([name, sites]) => ({
      ...sheet(name, sites),
      walks: groupBy(sites, MICRO)
        .filter(([w]) => !sectorFilter || w === sectorFilter)
        .map(([w, ws]) => sheet(w, ws)),
    })).filter((s) => !sectorFilter || s.walks!.length > 0);
  }, [data, layer, sectorFilter, slice]);

  const build = async () => {
    if (!data) return;
    // Claimed on the click: see reserveTab.
    const tab = reserveTab();
    setFailed(null);
    setBusy({ done: 0, total: sectors.length, label: 'cover' });
    try {
      const post = data.sites.find((s) => s.id === data.incident.command_post_site_id);
      const blob = await buildPacketPdf({
        ward: WARD_NAME,
        incident: data.incident,
        counts: data.counts,
        commandPost: post
          ? [post.label, post.formatted_address && stripHtml(post.formatted_address)]
            .filter(Boolean).join(' — ')
          : null,
        leadership: leadershipFrom((leaders.data?.results ?? []) as SearchPeopleRow[]),
        sectors,
        plat: (parcels.data as PlatData | undefined) ?? null,
        shelters: data.facilities
          .filter((f) => f.site_kind === 'shelter')
          .map((f) => ({
            label: f.label,
            formatted_address: f.formatted_address,
            capacity: f.capacity,
            people: f.people,
          })),
        sections,
        onProgress: (done, total, label) => setBusy({ done, total, label }),
      });
      openBlob(blob, packetFilename(data.incident.name), tab);
    } catch (e) {
      releaseTab(tab);
      setFailed(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  if (isLoading) return <p className="p-8 text-slate-500">Loading the incident…</p>;
  if (error || !data) return <p className="p-8 text-red-600">Error loading incident.</p>;

  const { incident } = data;
  const sheets = sectors.flatMap((s) => (s.walks?.length ? s.walks : [s]));
  const sites = sheets.reduce((n, s) => n + s.sites.length, 0);
  const people = sheets.reduce((n, s) => n + s.sites.reduce((m, x) => m + x.people.length, 0), 0);

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4">
      <header className="flex items-baseline justify-between">
        <div>
          <Link to={`/incidents/${id}`} className="text-sm text-slate-500 hover:underline">
            ← Board
          </Link>
          <h1 className="text-lg font-semibold">{incident.name} — packet</h1>
        </div>
        {incident.is_drill && (
          <span className="rounded bg-slate-900 px-2 py-0.5 text-xs font-bold uppercase tracking-widest text-white">
            Drill
          </span>
        )}
      </header>

      <section className="rounded border border-slate-200 p-3 dark:border-slate-700">
        <h2 className="mb-2 text-sm font-semibold">In the packet</h2>
        <div className="space-y-1 text-sm">
          {SECTIONS.map((s) => (
            <label key={s} className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={sections[s]}
                onChange={(e) => setSections((p) => ({ ...p, [s]: e.target.checked }))}
              />
              <span className="capitalize">{s}</span>
              <span className="text-xs text-slate-500 dark:text-slate-400">{BLURB[s]}</span>
            </label>
          ))}
        </div>
      </section>

      <section className="rounded border border-slate-200 p-3 dark:border-slate-700">
        <h2 className="mb-2 text-sm font-semibold">Cut the sectors on</h2>
        <div className="flex gap-2 text-sm">
          {[MICRO, MACRO].map((l) => (
            <button
              key={l}
              onClick={() => setParams((p) => {
                const next = new URLSearchParams(p);
                next.set('layer', l);
                return next;
              })}
              className={`rounded px-2 py-0.5 text-xs ${
                layer === l ? 'bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900'
                  : 'border border-slate-300 text-slate-600 dark:border-slate-600 dark:text-slate-300'}`}
            >
              {layerLabel(l)}
            </button>
          ))}
          {sectorFilter && (
            <span className="rounded bg-sky-100 px-2 py-0.5 text-xs text-sky-800">
              sector: {sectorFilter}
            </span>
          )}
          {slice && (
            <span className="rounded bg-sky-100 px-2 py-0.5 text-xs text-sky-800">
              slice: {slice.label}
            </span>
          )}
        </div>
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          {sectors.length} sector{sectors.length === 1 ? '' : 's'}
          {layer === MICRO && ` · ${sheets.length} walk${sheets.length === 1 ? '' : 's'}`}
          {' · '}{sites} addresses · {people} people
        </p>
      </section>

      <button
        onClick={build}
        disabled={!!busy}
        className="w-full rounded border border-slate-800 bg-slate-800 px-3 py-2 text-sm font-medium text-white disabled:opacity-50 dark:border-slate-200 dark:bg-slate-200 dark:text-slate-900"
      >
        {busy ? `${busy.done} of ${busy.total} — ${busy.label}` : 'Download the packet'}
      </button>

      {parcels.isError && (
        <p className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          Parcel data is unavailable, so the sector maps will be left out. The rosters still print.
        </p>
      )}
      {failed && <p className="text-xs text-red-600">{failed}</p>}
      <p className="text-xs text-slate-500 dark:text-slate-400">
        Printed cold, before anything happens: seed a planning incident, print this, put it in
        the go-bag. Every page says when it was printed, because paper goes stale.
      </p>
    </div>
  );
}

const BLURB: Record<Section, string> = {
  cover: 'status at printing, a tally to keep by hand, who is in command',
  info: 'contacts and comms — 911, utilities, the presidencies, the stake',
  sectors: 'a map and an accountability roster for each sector',
  log: 'blank activity sheets',
  shelters: 'a sign-in sheet per shelter, filled in as far as we know it',
};
